import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTodos, setFilters, setPage, setSelectedTodo, updateTodo, deleteTodo } from '../store/slices/todosSlice';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Badge } from '../components/common/Badge';
import { Spinner } from '../components/common/Spinner';
import { EmptyState } from '../components/common/EmptyState';
import { TodoDetail } from '../components/todos/TodoDetail';
import { CreateTodoModal } from '../components/todos/CreateTodoModal';
import { CheckCircle, Circle, Clock, Plus, Search, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export const TodosPage = () => {
  const dispatch = useDispatch();
  const { items, filters, pagination, loading, selectedTodo } = useSelector(state => state.todos);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Fetch todos on mount and when filters/pagination change
  useEffect(() => {
    dispatch(fetchTodos({ filters, pagination }));
  }, [dispatch, filters, pagination]);

  const handleSearch = () => {
    dispatch(setFilters({ search: searchInput }));
  };

  const handleFilterChange = (filterName, value) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  const handleToggleComplete = async (todo) => {
    try {
      const newStatus = todo.status === 'completed' ? 'pending' : 'completed';
      await dispatch(updateTodo({
        todoId: todo.id,
        updates: { status: newStatus }
      })).unwrap();
      toast.success(`Todo marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update todo');
    }
  };

  const handleDeleteTodo = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;

    try {
      await dispatch(deleteTodo(todoId)).unwrap();
      toast.success('Todo deleted');
    } catch (error) {
      toast.error('Failed to delete todo');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-mint-500" size={20} />;
      case 'in_progress':
        return <Clock className="text-yellow-500" size={20} />;
      default:
        return <Circle className="text-slate-400" size={20} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">
              My Todos
            </h1>
            <p className="text-slate-600">
              Manage your tasks and track agent execution progress
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus size={20} />
            New Todo
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search todos..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            <Button
              variant="outline"
              onClick={handleSearch}
              className="flex items-center gap-2"
            >
              <Search size={18} />
              Search
            </Button>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-periwinkle-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>

            {/* Priority Filter */}
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-periwinkle-500"
            >
              <option value="all">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            {/* Sort */}
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-periwinkle-500"
            >
              <option value="created_at">Date Created</option>
              <option value="due_date">Due Date</option>
              <option value="priority">Priority</option>
            </select>

            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-periwinkle-500"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Empty State */}
        {!loading && items.length === 0 && (
          <EmptyState
            icon={<CheckCircle size={64} />}
            title="No todos found"
            description="Create your first todo or adjust your filters to see existing todos."
            action={
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={20} className="mr-2" />
                Create Todo
              </Button>
            }
          />
        )}

        {/* Todo List */}
        {!loading && items.length > 0 && (
          <div className="space-y-3">
            {items.map((todo) => (
              <Card
                key={todo.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  dispatch(setSelectedTodo(todo));
                  setShowDetailModal(true);
                }}
              >
                <div className="flex items-start gap-4">
                  {/* Status Icon */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleComplete(todo);
                    }}
                    className="mt-1 hover:scale-110 transition-transform"
                  >
                    {getStatusIcon(todo.status)}
                  </button>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className={`text-lg font-medium ${
                          todo.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-800'
                        }`}>
                          {todo.title}
                        </h3>
                        {todo.description && (
                          <p className="text-sm text-slate-600 mt-1">
                            {todo.description.substring(0, 150)}
                            {todo.description.length > 150 && '...'}
                          </p>
                        )}
                      </div>
                      {todo.priority && (
                        <Badge variant={getPriorityColor(todo.priority)} className="ml-4">
                          {todo.priority}
                        </Badge>
                      )}
                    </div>

                    {/* Metadata */}
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      {todo.created_at && (
                        <span>Created: {format(new Date(todo.created_at), 'MMM d, yyyy')}</span>
                      )}
                      {todo.due_date && (
                        <span>Due: {format(new Date(todo.due_date), 'MMM d, yyyy')}</span>
                      )}
                      {todo.email_context && (
                        <Badge variant="info" className="text-xs">
                          From Email
                        </Badge>
                      )}
                      {todo.agent_execution_id && (
                        <Badge variant="primary" className="text-xs">
                          Agent Execution
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && items.length > 0 && pagination.total > pagination.perPage && (
          <div className="flex justify-center items-center gap-4 mt-6">
            <Button
              variant="outline"
              disabled={pagination.page === 1}
              onClick={() => dispatch(setPage(pagination.page - 1))}
            >
              Previous
            </Button>
            <span className="text-slate-600">
              Page {pagination.page} of {Math.ceil(pagination.total / pagination.perPage)}
            </span>
            <Button
              variant="outline"
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.perPage)}
              onClick={() => dispatch(setPage(pagination.page + 1))}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Create Todo Modal */}
      <CreateTodoModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Todo Detail Modal */}
      {selectedTodo && (
        <TodoDetail
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          todo={selectedTodo}
          onDelete={handleDeleteTodo}
        />
      )}
    </div>
  );
};
